import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { distinctUntilChanged, map } from 'rxjs';

import {
  Entreprise,
  InscriptionRdvPayload,
  RdvApiService,
  RdvSlotOption,
  SiteDetails,
  SitePrelevement,
} from '../../core/api/rdv-api.service';

/** Type de don pour le rendez-vous (plaquettes = inscription hors ligne uniquement). */
export type RdvDonationKind = 'sang' | 'plasma' | 'plaquettes';

const PARRAINAGE_LABEL_FR: Record<'non' | 'parrain' | 'filleul', string> = {
  non: 'Non',
  parrain: "Oui, j'accompagnerai un nouveau donneur",
  filleul: "Oui, je suis un nouveau donneur et je serai accompagné de mon parrain",
};

const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const WEEKDAY_LABEL_FR: Record<(typeof WEEKDAY_KEYS)[number], string> = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche',
};

/** En-têtes de colonnes calendrier (semaine commence lundi). */
const CALENDAR_WEEKDAY_HEADERS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'] as const;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Date locale YYYY-MM-DD (alignée sur les chaînes API). */
function localIsoFromYmd(y: number, month1to12: number, day: number): string {
  return `${y}-${pad2(month1to12)}-${pad2(day)}`;
}

function localIsoToday(): string {
  const n = new Date();
  return localIsoFromYmd(n.getFullYear(), n.getMonth() + 1, n.getDate());
}

/** Dernier mois (année + mois 0–11) présent dans la liste de dates ISO. */
function maxMonthFromIsoDates(list: string[]): { y: number; m0: number } {
  const now = new Date();
  let y = now.getFullYear();
  let m0 = now.getMonth();
  for (const iso of list) {
    const p = iso.split('-');
    if (p.length < 2) {
      continue;
    }
    const yy = Number(p[0]);
    const mm = Number(p[1]) - 1;
    if (Number.isNaN(yy) || Number.isNaN(mm) || mm < 0 || mm > 11) {
      continue;
    }
    if (yy > y || (yy === y && mm > m0)) {
      y = yy;
      m0 = mm;
    }
  }
  return { y, m0 };
}

export interface DatePickerCell {
  /** Clé stable pour @for (track). */
  key: string;
  iso: string | null;
  dayNum: number | null;
  selectable: boolean;
}

export interface DatePickerMonthBlock {
  trackId: string;
  title: string;
  weeks: DatePickerCell[][];
}

function emailsMatch(group: AbstractControl): ValidationErrors | null {
  const e = group.get('email');
  const c = group.get('emailConfirm');
  if (!e || !c) {
    return null;
  }
  const ev = String(e.value ?? '').trim();
  const cv = String(c.value ?? '').trim();
  if (!ev || !cv) {
    return null;
  }
  return ev === cv ? null : { emailsMismatch: true };
}

/** Au plus tard aujourd’hui − 18 ans (don de plasma / sang classique). */
function birthdateAdultMax(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (!v) {
      return null;
    }
    const birth = new Date(String(v));
    if (Number.isNaN(birth.getTime())) {
      return { invalidDate: true };
    }
    const limit = new Date();
    limit.setFullYear(limit.getFullYear() - 18);
    limit.setHours(23, 59, 59, 999);
    return birth <= limit ? null : { tooYoung: true };
  };
}

@Component({
  selector: 'app-inscription-rdv',
  imports: [ReactiveFormsModule],
  templateUrl: './inscription-rdv.html',
  styleUrl: './inscription-rdv.css',
})
export class InscriptionRdv implements OnInit {
  private readonly rdvApi = inject(RdvApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly sanitizer = inject(DomSanitizer);

  /** Modale « Faire le test » (iframe donneurdesang.be). */
  readonly testEligibilitySangModalOpen = signal(false);
  readonly testEligibilityPlasmaModalOpen = signal(false);
  readonly testEligibilitySangIframeSrc: SafeResourceUrl =
    this.sanitizer.bypassSecurityTrustResourceUrl('https://www.donneurdesang.be/widgets/fr/form-eligibilite-sang');
  readonly testEligibilityPlasmaIframeSrc: SafeResourceUrl =
    this.sanitizer.bypassSecurityTrustResourceUrl('https://www.donneurdesang.be/widgets/fr/form-eligibilite-plasma');

  readonly currentCompany = signal<Entreprise | null>(null);
  readonly loadingCompany = signal(true);
  readonly companyLoadError = signal<string | null>(null);
  /** Inscription ouverte pour cette entreprise (API `action_active`, défaut true si absent). */
  readonly companyActionActive = signal(true);

  readonly selectedEntrepriseId = signal<string | number | null>(null);
  readonly sitesError = signal<string | null>(null);
  readonly sitesByRegion = signal<{ region: string; sites: SitePrelevement[] }[]>([]);
  readonly siteFilter = signal('');
  readonly expandedRegions = signal<Record<string, boolean>>({});

  readonly selectedSiteId = signal<string | number | null>(null);
  readonly currentSite = signal<SitePrelevement | null>(null);
  readonly selectedRdvType = signal<RdvDonationKind | null>(null);
  readonly loadingDates = signal(false);
  readonly datesError = signal<string | null>(null);
  readonly siteDetails = signal<SiteDetails | null>(null);
  readonly availableDates = signal<string[]>([]);

  /** Mois affiché dans le calendrier (0 = premier mois de la plage). */
  readonly calendarMonthViewIndex = signal(0);

  readonly selectedDate = signal<string | null>(null);
  readonly loadingSlots = signal(false);
  readonly slotsError = signal<string | null>(null);
  readonly slotsDateLabel = signal('');
  readonly slots = signal<RdvSlotOption[]>([]);
  readonly withoutRdvHourBegin = signal<string>('');
  readonly withoutRdvHourEnd = signal<string>('');

  readonly selectedTime = signal<string | null>(null);

  readonly submitting = signal(false);
  readonly submittedSuccess = signal(false);
  readonly apiError = signal<string | null>(null);

  /** Étape affichée (1 = lieu, 2 = type de don, … 5 = coordonnées). */
  readonly currentStep = signal<1 | 2 | 3 | 4 | 5>(1);

  readonly wizardStepCount = 5;

  readonly detailsForm = this.fb.group(
    {
      lastname: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      firstname: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      email: this.fb.nonNullable.control('', { validators: [Validators.required, Validators.email] }),
      emailConfirm: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      phone: this.fb.nonNullable.control('', { validators: [Validators.required] }),
      birthdate: this.fb.nonNullable.control('', {
        validators: [Validators.required, birthdateAdultMax()],
      }),
      hasAlreadyDonated: this.fb.control<'OUI' | 'NON' | null>(null, {
        validators: [Validators.required],
      }),
      hasAlreadyDonatedPlasma: this.fb.control<'OUI' | 'NON' | null>(null, {
        validators: [Validators.required],
      }),
      parrainage: this.fb.nonNullable.control<'non' | 'parrain' | 'filleul'>('non'),
      acceptDonnees: this.fb.nonNullable.control(false, { validators: [Validators.requiredTrue] }),
    },
    { validators: [emailsMatch] },
  );

  readonly filteredRegions = computed(() => {
    const q = this.siteFilter().toLowerCase().trim();
    const groups = this.sitesByRegion();
    if (!q) {
      return groups;
    }
    return groups
      .map(({ region, sites }) => ({
        region,
        sites: sites.filter((s) => s.name.toLowerCase().includes(q)),
      }))
      .filter((g) => g.sites.length > 0);
  });

  /** Lignes « jour → horaires » pour l’affichage (ordre lundi → dimanche). */
  readonly siteOpeningHoursRows = computed((): { label: string; hours: string }[] => {
    const days = this.siteDetails()?.days;
    if (!days) {
      return [];
    }
    const rows: { label: string; hours: string }[] = [];
    for (const key of WEEKDAY_KEYS) {
      const hours = days[key];
      if (typeof hours === 'string' && hours.trim() !== '') {
        rows.push({ label: WEEKDAY_LABEL_FR[key], hours });
      }
    }
    return rows;
  });

  /** Calendriers du mois en cours → dernier mois couvert par l’API. */
  readonly datePickerCalendar = computed((): DatePickerMonthBlock[] => {
    const list = this.availableDates();
    const availableSet = new Set(list);
    const todayIso = localIsoToday();
    const now = new Date();
    const { y: endY, m0: endM } = maxMonthFromIsoDates(list);

    let y = now.getFullYear();
    let m0 = now.getMonth();
    const blocks: DatePickerMonthBlock[] = [];

    while (y < endY || (y === endY && m0 <= endM)) {
      const trackId = `${y}-${m0}`;
      blocks.push({
        trackId,
        ...this.buildDatePickerMonth(y, m0, availableSet, todayIso),
      });
      m0 += 1;
      if (m0 > 11) {
        m0 = 0;
        y += 1;
      }
    }

    return blocks;
  });

  /** Bloc calendrier du mois courant (navigation flèches). */
  readonly displayedCalendarMonthBlock = computed((): DatePickerMonthBlock | null => {
    const blocks = this.datePickerCalendar();
    const i = this.calendarMonthViewIndex();
    if (blocks.length === 0 || i < 0 || i >= blocks.length) {
      return null;
    }
    return blocks[i];
  });

  private readonly syncCalendarMonthViewIndex = effect(() => {
    const len = this.datePickerCalendar().length;
    const idx = this.calendarMonthViewIndex();
    untracked(() => {
      if (len === 0) {
        if (idx !== 0) {
          this.calendarMonthViewIndex.set(0);
        }
        return;
      }
      if (idx >= len) {
        this.calendarMonthViewIndex.set(len - 1);
      }
      if (idx < 0) {
        this.calendarMonthViewIndex.set(0);
      }
    });
  });

  readonly calendarWeekdayHeaders = CALENDAR_WEEKDAY_HEADERS;

  /** Libellé affiché du créneau choisi (ex. « 14:30 »), pour le récapitulatif après envoi. */
  readonly selectedSlotLabel = computed(() => {
    const t = this.selectedTime();
    if (t == null || t === '') {
      return '';
    }
    const slot = this.slots().find((s) => s.value === t);
    return slot?.label ?? String(t);
  });

  /** Libellé court du type de don (sous l’étape 2 dans le stepper). */
  readonly selectedRdvTypeLabel = computed(() => {
    const k = this.selectedRdvType();
    if (k === 'sang') {
      return 'Sang';
    }
    if (k === 'plasma') {
      return 'Plasma';
    }
    if (k === 'plaquettes') {
      return 'Plaquettes';
    }
    return '';
  });

  /** Dernière étape accessible (les plaquettes ne déverrouillent pas la date en ligne). */
  readonly maxUnlockedStep = computed((): 1 | 2 | 3 | 4 | 5 => {
    const t = this.selectedTime();
    if (t != null && t !== '') {
      return 5;
    }
    if (this.selectedDate() != null) {
      return 4;
    }
    const kind = this.selectedRdvType();
    if (this.selectedSiteId() != null && (kind === 'sang' || kind === 'plasma')) {
      return 3;
    }
    if (this.selectedSiteId() != null) {
      return 2;
    }
    return 1;
  });

  /** Remplissage de la barre selon l’étape affichée (1 → 20 % … 5 → 100 %). */
  readonly wizardProgressPercent = computed(() => (this.currentStep() / this.wizardStepCount) * 100);

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    if (this.testEligibilitySangModalOpen()) {
      this.closeTestEligibilitySangModal();
    }
    if (this.testEligibilityPlasmaModalOpen()) {
      this.closeTestEligibilityPlasmaModal();
    }
  }

  openTestEligibilitySangModal(): void {
    this.testEligibilitySangModalOpen.set(true);
  }

  closeTestEligibilitySangModal(): void {
    this.testEligibilitySangModalOpen.set(false);
  }

  openTestEligibilityPlasmaModal(): void {
    this.testEligibilityPlasmaModalOpen.set(true);
  }

  closeTestEligibilityPlasmaModal(): void {
    this.testEligibilityPlasmaModalOpen.set(false);
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((pm) => pm.get('slug')?.trim() ?? ''),
        distinctUntilChanged(),
      )
      .subscribe((slug: string) => {
        if (!slug) {
          this.loadingCompany.set(false);
          this.companyLoadError.set('Lien invalide : entreprise non précisée.');
          this.currentCompany.set(null);
          this.selectedEntrepriseId.set(null);
          this.companyActionActive.set(true);
          return;
        }

        this.companyLoadError.set(null);
        this.companyActionActive.set(true);
        this.loadingCompany.set(true);
        this.currentCompany.set(null);
        this.resetStepsAfterEntreprise();
        this.selectedEntrepriseId.set(null);

        this.rdvApi.getEntrepriseBySlug(slug).subscribe({
          next: (res) => {
            const entreprise = res.entreprise;
            this.currentCompany.set(entreprise);
            this.selectedEntrepriseId.set(entreprise.id);

            const sites = res.sites;
            this.loadingCompany.set(false);
            this.companyActionActive.set(res.action_active !== false);
            this.applySitesFromResponse(sites);
          },
          error: (err: unknown) => {
            this.loadingCompany.set(false);
            this.companyLoadError.set(
              err instanceof Error ? err.message : 'Impossible de charger cette entreprise.',
            );
          },
        });
      });
  }

  protected onSiteFilterInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.siteFilter.set(v);
  }

  private applySitesFromResponse(sites: SitePrelevement[]): void {
    this.sitesError.set(null);
    const filtered = sites.filter((s) => s.type_plasma == null || s.type_plasma === 1);
    const grouped = this.groupSitesByRegion(filtered);
    this.sitesByRegion.set(grouped);
    const expanded: Record<string, boolean> = {};
    for (const g of grouped) {
      expanded[g.region] = false;
    }
    this.expandedRegions.set(expanded);
    if (filtered.length === 0) {
      this.sitesError.set('Aucun lieu de prélèvement pour cette entreprise.');
    }
  }

  private buildDatePickerMonth(
    year: number,
    monthIndex: number,
    availableSet: ReadonlySet<string>,
    todayIso: string,
  ): Pick<DatePickerMonthBlock, 'title' | 'weeks'> {
    const first = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    const startPad = (first.getDay() + 6) % 7;

    const title = new Intl.DateTimeFormat('fr-BE', {
      month: 'long',
      year: 'numeric',
    }).format(first);

    const flat: DatePickerCell[] = [];
    for (let i = 0; i < startPad; i++) {
      flat.push({
        key: `p-${year}-${monthIndex}-s${i}`,
        iso: null,
        dayNum: null,
        selectable: false,
      });
    }
    for (let d = 1; d <= lastDay; d++) {
      const iso = localIsoFromYmd(year, monthIndex + 1, d);
      const inList = availableSet.has(iso);
      const notPast = iso >= todayIso;
      flat.push({
        key: iso,
        iso,
        dayNum: d,
        selectable: inList && notPast,
      });
    }
    let trail = 0;
    while (flat.length % 7 !== 0) {
      flat.push({
        key: `p-${year}-${monthIndex}-t${trail++}`,
        iso: null,
        dayNum: null,
        selectable: false,
      });
    }

    const weeks: DatePickerCell[][] = [];
    for (let i = 0; i < flat.length; i += 7) {
      weeks.push(flat.slice(i, i + 7));
    }

    return { title, weeks };
  }

  protected toggleRegion(region: string): void {
    const cur = { ...this.expandedRegions() };
    cur[region] = !cur[region];
    this.expandedRegions.set(cur);
  }

  protected isRegionExpanded(region: string): boolean {
    return !!this.expandedRegions()[region];
  }

  protected selectSite(site: SitePrelevement): void {
    const siteId = site.id;
    // Évite un 2e appel API si le clic est dupliqué (label + radio / double dispatch).
    if (this.loadingDates() && this.selectedSiteId() === siteId) {
      this.currentStep.set(2);
      return;
    }
    this.selectedSiteId.set(siteId);
    this.currentSite.set(site);
    this.selectedRdvType.set(null);
    this.currentStep.set(2);

    this.selectedDate.set(null);
    this.availableDates.set([]);
    this.datesError.set(null);

    this.slots.set([]);
    this.slotsDateLabel.set('');
    this.slotsError.set(null);
    
    this.selectedTime.set(null);
    this.loadingDates.set(true);

    let lang = 'FR';

    this.rdvApi.getDatesBySite(siteId, lang).subscribe({
      next: (res) => {
        this.loadingDates.set(false);
        this.siteDetails.set(res.site);
        const sorted = [...res.availableDates].sort();
        this.availableDates.set(sorted);
        this.calendarMonthViewIndex.set(0);
        this.datesError.set(
          sorted.length === 0 ? 'Aucune date disponible pour ce lieu.' : null,
        );
      },
      error: (err: unknown) => {
        this.loadingDates.set(false);
        this.siteDetails.set(null);
        this.availableDates.set([]);
        this.calendarMonthViewIndex.set(0);
        this.datesError.set(
          err instanceof Error ? err.message : 'Erreur lors du chargement des dates.',
        );
      },
    });
  }

  protected isSiteSelected(siteId: string | number): boolean {
    return this.selectedSiteId() === siteId;
  }

  protected isRdvTypeSelected(kind: RdvDonationKind): boolean {
    return this.selectedRdvType() === kind;
  }

  protected selectRdvType(kind: RdvDonationKind): void {
    const prev = this.selectedRdvType();
    this.selectedRdvType.set(kind);

    if (kind === 'plaquettes') {
      this.selectedDate.set(null);
      this.selectedTime.set(null);
      this.slots.set([]);
      this.slotsDateLabel.set('');
      this.slotsError.set(null);
      this.loadingSlots.set(false);
      this.currentStep.set(2);
      return;
    }

    if (prev !== kind) {
      this.selectedDate.set(null);
      this.selectedTime.set(null);
      this.slots.set([]);
      this.slotsDateLabel.set('');
      this.slotsError.set(null);
      this.loadingSlots.set(false);
    }

    const siteId = this.selectedSiteId();
    if (siteId == null) {
      this.currentStep.set(3);
      return;
    }

    const lang = 'FR';
    this.loadingDates.set(true);
    this.datesError.set(null);
    this.rdvApi.getDatesBySite(siteId, lang, kind).subscribe({
      next: (res) => {
        this.loadingDates.set(false);
        this.siteDetails.set(res.site);
        const sorted = [...res.availableDates].sort();
        this.availableDates.set(sorted);
        this.calendarMonthViewIndex.set(0);
        this.datesError.set(
          sorted.length === 0 ? 'Aucune date disponible pour ce lieu.' : null,
        );
        this.currentStep.set(3);
      },
      error: (err: unknown) => {
        this.loadingDates.set(false);
        this.availableDates.set([]);
        this.calendarMonthViewIndex.set(0);
        this.datesError.set(
          err instanceof Error ? err.message : 'Erreur lors du chargement des dates.',
        );
        this.currentStep.set(3);
      },
    });
  }

  /** Lien tel: utilisable pour le bouton d’appel (espaces retirés). */
  protected siteTelHref(phone: string): string {
    const trimmed = phone.trim();
    if (!trimmed) {
      return '#';
    }
    return `tel:${trimmed.replace(/\s/g, '')}`;
  }

  /** Clé @for pour une ligne de semaine (Angular n’autorise pas `block` dans track). */
  protected weekTrackKey(week: DatePickerCell[]): string {
    return week.map((c) => c.key).join('|');
  }

  protected calendarMonthPrev(): void {
    this.calendarMonthViewIndex.update((i) => Math.max(0, i - 1));
  }

  protected calendarMonthNext(): void {
    const max = this.datePickerCalendar().length - 1;
    if (max < 0) {
      return;
    }
    this.calendarMonthViewIndex.update((i) => Math.min(max, i + 1));
  }

  protected canCalendarMonthPrev(): boolean {
    return this.calendarMonthViewIndex() > 0;
  }

  protected canCalendarMonthNext(): boolean {
    const len = this.datePickerCalendar().length;
    return len > 1 && this.calendarMonthViewIndex() < len - 1;
  }

  protected pickCalendarDate(cell: DatePickerCell): void {
    if (!cell.iso || !cell.selectable) {
      return;
    }
    this.selectDate(cell.iso);
  }

  protected selectDate(date: string): void {
    this.selectedDate.set(date);
    this.currentStep.set(4);
    this.selectedTime.set(null);
    this.slots.set([]);
    this.slotsError.set(null);
    const siteId = this.selectedSiteId();
    if (siteId == null) {
      return;
    }
    this.loadingSlots.set(true);
    this.rdvApi.getSlotsBySiteAndDate(siteId, date, this.selectedRdvType() ?? 'sang').subscribe({
      next: (res) => {
        this.loadingSlots.set(false);
        this.slotsDateLabel.set(res.dateLabel);
        this.slots.set(res.slots);
        this.withoutRdvHourBegin.set(res.withoutRdvHourBegin);
        this.withoutRdvHourEnd.set(res.withoutRdvHourEnd);
        this.slotsError.set(
          res.slots.length === 0 ? 'Aucun créneau disponible pour ce jour.' : null,
        );
      },
      error: (err: unknown) => {
        this.loadingSlots.set(false);
        this.slots.set([]);
        this.slotsDateLabel.set('');
        this.withoutRdvHourBegin.set('');
        this.withoutRdvHourEnd.set('');
        this.slotsError.set(
          err instanceof Error ? err.message : 'Erreur lors du chargement des créneaux.',
        );
      },
    });
  }

  protected isDateSelected(date: string): boolean {
    return this.selectedDate() === date;
  }

  protected selectTime(value: string): void {
    const slot = this.slots().find((s) => s.value === value);
    if (slot?.total === null) {
      return;
    }
    this.selectedTime.set(value);
    this.currentStep.set(5);
  }

  protected goToWizardStep(step: number): void {
    if (step < 1 || step > this.wizardStepCount) {
      return;
    }
    const max = this.maxUnlockedStep();
    if (step > max) {
      return;
    }
    this.currentStep.set(step as 1 | 2 | 3 | 4 | 5);
  }

  protected isWizardStepActive(step: number): boolean {
    return this.currentStep() === step;
  }

  protected isWizardStepReachable(step: number): boolean {
    return step >= 1 && step <= this.wizardStepCount && step <= this.maxUnlockedStep();
  }

  protected isTimeSelected(value: string): boolean {
    return this.selectedTime() === value;
  }

  /** E-mail saisi, pour le message de confirmation (évite les espaces en trop dans le template). */
  protected confirmationEmailDisplay(): string {
    return String(this.detailsForm.get('email')?.value ?? '').trim();
  }

  /** Libellé du choix parrainage pour le récapitulatif. */
  protected parrainageRecapLabel(): string {
    const v = this.detailsForm.get('parrainage')?.value ?? 'non';
    return PARRAINAGE_LABEL_FR[v as keyof typeof PARRAINAGE_LABEL_FR] ?? PARRAINAGE_LABEL_FR.non;
  }

  /** Pour les attributs [value] des <select> / <option> (pas de `String` global dans les templates). */
  protected asText(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  protected formatDateFr(iso: string): string {
    const parts = iso.split('-').map((p) => Number(p));
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
      return iso;
    }
    const [y, m, d] = parts;
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('fr-BE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  protected showDetailsBlock(): boolean {
    return this.selectedTime() != null && this.selectedTime() !== '';
  }

  protected onSubmitDetails(): void {
    this.apiError.set(null);
    if (this.detailsForm.invalid) {
      this.detailsForm.markAllAsTouched();
      return;
    }
    const entrepriseId = this.selectedEntrepriseId();
    const siteId = this.selectedSiteId();
    const date = this.selectedDate();
    const time = this.selectedTime();
    const rdvKind = this.selectedRdvType();
    if (
      entrepriseId == null ||
      siteId == null ||
      !date ||
      !time ||
      (rdvKind !== 'sang' && rdvKind !== 'plasma')
    ) {
      this.apiError.set('Choisissez un lieu, un type de don (sang ou plasma), une date et un horaire.');
      return;
    }
    const d = this.detailsForm.getRawValue();
    const payload: InscriptionRdvPayload = {
      entrepriseId,
      siteId,
      type: rdvKind,
      date,
      time,
      lastname: d.lastname.trim(),
      firstname: d.firstname.trim(),
      email: d.email.trim(),
      phone: d.phone.trim(),
      birthdate: d.birthdate,
      hasAlreadyDonated: d.hasAlreadyDonated!,
      hasAlreadyDonatedPlasma: d.hasAlreadyDonatedPlasma!,
      parrainage: d.parrainage === 'non' ? '' : d.parrainage,
      acceptDonnees: d.acceptDonnees,
    };

    this.submitting.set(true);
    this.rdvApi.createRdv(payload).subscribe({
      next: (res) => {
        this.submitting.set(false);
        if (res.success) {
          this.submittedSuccess.set(true);
          return;
        }
        this.apiError.set(res.error?.trim() || 'Une erreur est survenue.');
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        if (err instanceof HttpErrorResponse) {
          const body = err.error;
          if (body && typeof body === 'object' && body !== null) {
            if ('success' in body && body.success === false && 'error' in body) {
              const msg = (body as { error?: unknown }).error;
              if (typeof msg === 'string' && msg.trim()) {
                this.apiError.set(msg.trim());
                return;
              }
            }
            if ('error' in body) {
              const msg = (body as { error?: unknown }).error;
              if (typeof msg === 'string' && msg.trim()) {
                this.apiError.set(msg.trim());
                return;
              }
            }
          }
        }
        if (err instanceof Error && err.message) {
          this.apiError.set(err.message);
          return;
        }
        this.apiError.set("Impossible d'envoyer le formulaire. Réessaie plus tard.");
      },
    });
  }

  private resetStepsAfterEntreprise(): void {
    this.currentStep.set(1);
    this.sitesByRegion.set([]);
    this.sitesError.set(null);
    this.siteFilter.set('');
    this.expandedRegions.set({});
    this.selectedSiteId.set(null);
    this.selectedRdvType.set(null);
    this.siteDetails.set(null);
    this.availableDates.set([]);
    this.calendarMonthViewIndex.set(0);
    this.datesError.set(null);
    this.loadingDates.set(false);
    this.selectedDate.set(null);
    this.slots.set([]);
    this.withoutRdvHourBegin.set('');
    this.withoutRdvHourEnd.set('');
    this.slotsDateLabel.set('');
    this.slotsError.set(null);
    this.loadingSlots.set(false);
    this.selectedTime.set(null);
  }

  private groupSitesByRegion(sites: SitePrelevement[]): { region: string; sites: SitePrelevement[] }[] {
    const map = new Map<string, SitePrelevement[]>();
    for (const s of sites) {
      const r = (s.region?.trim() || 'Autres').trim();
      if (!map.has(r)) {
        map.set(r, []);
      }
      map.get(r)!.push(s);
    }
    return [...map.entries()]
      .map(([region, list]) => ({ region, sites: list }))
      .sort((a, b) => a.region.localeCompare(b.region, 'fr'));
  }
}
